import { useTranslations } from 'next-intl';
import { useRef, useState } from 'react';
import { UPLOAD_SCENE, type UploadScene } from '@/app/constants';
import type { ImagePrompt, ImagePromptWithSize, ImageRange } from '@/app/types';
import { checkSourceImageSize, getImageSize } from '@/app/utils';
import { handleChangeImageRange } from '@/app/utils/client';
import { toast } from '../components/ui/use-toast';

interface UseImageSelectModalOptions {
  updateSelectImage: (idx: number, newImagePrompt: ImagePrompt) => void;
  maxImageSize: number;
  scene?: UploadScene;
}

export const useImageSelectModal = ({
  updateSelectImage,
  maxImageSize,
  scene = UPLOAD_SCENE.VIDU,
}: UseImageSelectModalOptions) => {
  const t = useTranslations();

  const [selectModalImageSource, setSelectModalImageSource] = useState<ImagePromptWithSize | null>(null);
  const selectingReferenceIdx = useRef<number | null>(null);

  const handleOpenSelectModal = async (idx: number, imagePrompt?: ImagePrompt | null) => {
    if (!imagePrompt) return;
    if (!checkSourceImageSize(imagePrompt)) {
      try {
        const { width, height } = await getImageSize(imagePrompt.sourceImagePreview);
        imagePrompt.sourceImageWidth = width;
        imagePrompt.sourceImageHeight = height;
      } catch {
        toast({
          type: 'error',
          title: t('create.char2VideoForm.imageError', { index: idx + 1 }),
        });
        return;
      }
    }

    selectingReferenceIdx.current = idx;
    setSelectModalImageSource(imagePrompt as ImagePromptWithSize);
  };

  const handleCloseSelectModal = () => {
    setSelectModalImageSource(null);
    selectingReferenceIdx.current = null;
  };

  const handleSaveSelectImage = async (range: ImageRange, cropRatio?: number | 'free') => {
    if (!selectModalImageSource || selectingReferenceIdx.current === null) return;
    const result = await handleChangeImageRange({
      range,
      imagePrompt: selectModalImageSource,
      maxImageSize,
      scene,
    });
    if (!result) return;

    // 检查上传错误
    if ('error' in result && result.error) {
      const error = result.error;
      if (error.reason === 'AuditSubmitIllegal') {
        toast({
          type: 'error',
          message: t('create.createForm.upload.error.AuditSubmitIllegal'),
        });
      } else {
        toast({
          type: 'error',
          message: error.message || t('loadImageError'),
        });
      }
      return;
    }

    // TypeScript 类型守卫：此时 result 一定是 { imagePrompt: ImagePrompt }
    if ('imagePrompt' in result) {
      const newImagePrompt = result.imagePrompt;
      // 保存选择的裁剪比例
      if (cropRatio !== undefined) {
        newImagePrompt.cropRatio = cropRatio;
      }
      updateSelectImage(selectingReferenceIdx.current, newImagePrompt); // 把裁剪后的图片更新出去
      handleCloseSelectModal(); // 关闭选择弹窗
    }
  };

  return {
    selectModalImageSource,
    handleOpenSelectModal,
    handleCloseSelectModal,
    handleSaveSelectImage,
  };
};
